import { Chip, MenuButton, type MenuButtonItem } from "@assetwatch/ui";
import type { WorkOrderId } from "../../lib/workIds";
import { useWorkOrderUpdateMutation } from "../../queries/useWorkOrderUpdateMutation";
import { workOrderStatusTone } from "../snowflakes/work-orders/presentation";

/**
 * Menu labels for the status targets the API can return. `COMPLETED` reads as
 * "Done" to match the POC status menu; it opens the Complete action rather
 * than changing the status directly.
 */
const STATUS_TARGET_LABELS: Record<string, string> = {
  OPEN: "Open",
  IN_PROGRESS: "In progress",
  ON_HOLD: "On hold",
  COMPLETED: "Done",
};

export type WorkOrderStatusMenuWorkOrder = {
  id: WorkOrderId;
  number: string;
  version: string;
  status: { code: string; name: string };
  statusTargets: string[];
};

/**
 * The Work Order status control from the POC's status quick edit
 * (`app/screens/wo-detail.jsx`'s `WoStatusQuickEdit`): the current status opens
 * a menu of the moves the server allows for this caller. With no allowed
 * moves it renders the read-only status chip instead.
 */
export function WorkOrderStatusMenu({
  workOrder,
  onRequestComplete,
  onUpdateError,
  completionPending = false,
}: {
  workOrder: WorkOrderStatusMenuWorkOrder;
  onRequestComplete: () => void;
  onUpdateError?: () => void;
  completionPending?: boolean;
}) {
  const updateMutation = useWorkOrderUpdateMutation();

  if (workOrder.statusTargets.length === 0) {
    return (
      <Chip
        label={workOrder.status.name}
        tone={workOrderStatusTone[workOrder.status.code] ?? "neutral"}
      />
    );
  }

  const items: MenuButtonItem[] = workOrder.statusTargets.map((code) => ({
    id: code,
    label: STATUS_TARGET_LABELS[code] ?? code,
    onSelect: () => {
      if (code === "COMPLETED") {
        onRequestComplete();
        return;
      }
      updateMutation.mutate(
        {
          workOrderId: workOrder.id,
          workOrderNumber: workOrder.number,
          version: workOrder.version,
          statusCode: code,
        },
        // Reload so a stale version or revoked access is not retried with
        // the same data.
        { onError: onUpdateError },
      );
    },
  }));

  return (
    /* @ds-deviation: the POC status selector is an outline pill tinted per
       status (WO_STATUS_STYLE in app/shared-status.jsx). MenuButton has no
       tone, so the trigger uses its neutral outline style and the status
       colour is not shown while the menu is available — proposed resolution:
       promote a tone (or an outline Chip trigger) into the design system. */
    <MenuButton
      align="start"
      aria-label={`${workOrder.status.name}, change status`}
      disabled={updateMutation.isPending || completionPending}
      items={items}
      label={workOrder.status.name}
      size="sm"
    />
  );
}
